/**
 * Unified Auth Composable
 *
 * Wraps Auth0 and adds dev-token fallback for ROPG-based dev logins.
 * When a dev-login token is stored in localStorage (from the quick-login flow),
 * this composable provides it via getAccessTokenSilently() so all API services
 * work without modification.
 */
import { computed } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { isTokenExpired } from '../utils/authErrorHandler'

const DEV_TOKEN_KEY = 'dev_login_token'

/** Store a dev-login access token for session persistence */
export function setDevToken(token: string) {
    localStorage.setItem(DEV_TOKEN_KEY, token)
}

/** Retrieve the stored dev-login token (null if not present) */
export function getDevToken(): string | null {
    return localStorage.getItem(DEV_TOKEN_KEY)
}

/** Clear the stored dev-login token */
export function clearDevToken() {
    localStorage.removeItem(DEV_TOKEN_KEY)
}

export function useAuth() {
    const auth0 = useAuth0()
    const userStore = useUserStore()

    const isAuthenticated = computed(() =>
        auth0.isAuthenticated.value || userStore.isAuthenticated
    )

    const getAccessTokenSilently = async (...args: Parameters<typeof auth0.getAccessTokenSilently>) => {
        // If actively impersonating (in-memory state set), use the impersonation token
        if (userStore.isImpersonating) {
            const impToken = localStorage.getItem('impersonation_token')
            if (impToken) {
                // Check if impersonation token is expired (1h TTL)
                if (isTokenExpired(impToken)) {
                    // Token expired — clean up and redirect to admin
                    localStorage.removeItem('impersonation_token')
                    localStorage.removeItem('impersonation_original_token')
                    // Force reload to /admin for a clean state reset
                    window.location.href = '/admin'
                    throw new Error('Impersonation session expired')
                }
                return impToken
            }
        }
        // Note: We intentionally do NOT clean up orphaned impersonation tokens here.
        // The else branch (isImpersonating=false) can fire during normal operation due
        // to Pinia reactivity timing. Orphan cleanup happens in handleAuthError() and
        // on explicit logout instead.

        // If Auth0 SDK is authenticated, use it
        if (auth0.isAuthenticated.value) {
            return auth0.getAccessTokenSilently(...args)
        }

        // Fallback: use stored dev-login token
        const devToken = getDevToken()
        if (devToken) {
            return devToken
        }

        // Neither available — let Auth0 SDK throw its normal error
        return auth0.getAccessTokenSilently(...args)
    }

    return {
        ...auth0,
        isAuthenticated,
        getAccessTokenSilently,
    }
}
